import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MoreIcon } from "@/shared/assets/icons";
import { Button, Dropdown, DropdownItem } from "@/shared/ui";
import { PERMISSIONS } from "@/shared/consts";
import { useAuth } from "@/shared/hooks";
import type { InventoryUnit } from "../types";
import { getWarehouseAvailableActions } from "../utils";

interface Props {
  item: InventoryUnit;
  onAssign: (id: number) => void;
  onReturn: (id: number) => void;
  onWriteOff: (id: number) => void;
  onTransfer?: (item: InventoryUnit) => void;
  onRevalue?: (item: InventoryUnit) => void;
  assigningId?: number | null;
  writingOffId?: number | null;
  isOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
  direction?: "top" | "bottom";
}

export default function WarehouseActions({
  item,
  onAssign,
  onReturn,
  onWriteOff,
  onTransfer,
  onRevalue,
  assigningId,
  writingOffId,
  isOpen: controlledOpen,
  onToggle,
  direction = "bottom",
}: Props) {
  const { t } = useTranslation("WarehousePage");
  const { hasPermission } = useAuth();
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setIsOpen = onToggle || setInternalOpen;

  const availableActions = getWarehouseAvailableActions(item.asset_type, item.status, item.is_depreciable, item.inventory_subtype);
  const canManageInventory = hasPermission(PERMISSIONS.WAREHOUSE.EDIT_INVENTORY);
  const canAssignAssets = hasPermission(PERMISSIONS.WAREHOUSE.ASSIGN_ASSETS);
  const canReturnAssets = hasPermission(PERMISSIONS.WAREHOUSE.RETURN_ASSETS);
  const canWriteOff = hasPermission(PERMISSIONS.WAREHOUSE.WRITE_OFF);

  const permissionByAction: Record<string, boolean> = {
    assign: canAssignAssets,
    transfer: canAssignAssets,
    return: canReturnAssets,
    writeOff: canWriteOff,
    revalue: canManageInventory,
  };

  const actionsWithoutOpen = availableActions.filter(
    (action) => action !== "open" && permissionByAction[action],
  );
  const isAssigning = assigningId === item.id;
  const isWritingOff = writingOffId === item.id;
  const isAnyLoading = isAssigning || isWritingOff;

  if (actionsWithoutOpen.length === 0) {
    return null;
  }

  function handleAction(action: () => void) {
    action();
    setIsOpen(false);
  }

  return (
    <Dropdown open={isOpen && !isAnyLoading} onClose={() => setIsOpen(false)} direction={direction}>
      <Button
        variant="secondary"
        isIconButton
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        disabled={isAnyLoading}
        className="w-8! radius-xs! p-0!">
        <MoreIcon />
      </Button>

      {availableActions.includes("assign") && canAssignAssets && (
        <DropdownItem onClick={() => handleAction(() => onAssign(item.id))} disabled={isAssigning}>
          {isAssigning ? t("loading") : t("actions.assign")}
        </DropdownItem>
      )}

      {availableActions.includes("return") && canReturnAssets && (
        <DropdownItem onClick={() => handleAction(() => onReturn(item.id))}>{t("actions.return")}</DropdownItem>
      )}

      {availableActions.includes("transfer") && canAssignAssets && onTransfer && (
        <DropdownItem onClick={() => handleAction(() => onTransfer(item))}>
          {t("actions.transfer")}
        </DropdownItem>
      )}

      {availableActions.includes("writeOff") && canWriteOff && (
        <DropdownItem onClick={() => handleAction(() => onWriteOff(item.id))} disabled={isWritingOff}>
          {isWritingOff ? t("loading") : t("actions.writeOff")}
        </DropdownItem>
      )}

      {availableActions.includes("revalue") && canManageInventory && onRevalue && (
        <DropdownItem onClick={() => handleAction(() => onRevalue(item))}>
          {t("actions.revalue")}
        </DropdownItem>
      )}
    </Dropdown>
  );
}
